import { OverlapBlock, ElementPosition } from "types/injector";

/**
 * Returns the overlap block that this current index appears in, or null
 * @param current The index to check
 * @param blocks The overlap blocks that this index might be in
 */
export const inOverlapBlock = (
  current: number,
  blocks: OverlapBlock[]
): OverlapBlock | null => {
  // The overlap blocks are a map of elements that overlap because
  // of CSS floats. It describes the Y position where those elements start
  // and end. If the current element falls between the start and end, it is
  // affected by the float and we probably shouldn't inject anything there.
  for (const block of blocks) {
    if (current >= block.start && current <= block.end) {
      return block;
    }
  }
  return null;
};

/**
 * Find the start of the overlap block
 */
const findStart = (current: number, positions: ElementPosition[]): number => {
  // we're already at the start of the article
  if (current == 0) {
    return 0;
  }

  // we already know the current i position intersects with its next sibling,
  // so find the highest Y value of the two.
  // higher = higher on the page. higher = a smaller Y coordinate
  let highestYOfBlock = Math.min(
    positions[current].top,
    positions[current + 1]?.top
  );
  let start = current;

  // travel upwards through the elements
  for (let i = current - 1; i > -1; i--) {
    // the bottom of the element is within the existing top of the block, so it's within the block
    if (positions[i].bottom > highestYOfBlock) {
      start = i;
      // the top of this element is higher than the current known top of block
      if (positions[i].top < highestYOfBlock) {
        highestYOfBlock = positions[i].top;
      }
    }
    // the bottom of the element is above the overlap block, we are free.
    else {
      break;
    }
  }
  return start;
};

/**
 * Find the end of the overlap block
 */
const findEnd = (current: number, positions: ElementPosition[]): number => {
  // we already know the current i position intersects with its next sibling,
  // so find the lowest Y value of the two.
  // lower = lower on the page. lower = a larger Y coordinate
  let lowestYOfBlock = Math.max(
    positions[current].bottom,
    positions[current + 1]?.bottom
  );
  let end = current + 1;

  // travel downwards through the elements
  for (let i = current + 2; i < positions.length; i++) {
    // the top of the element is within the existing block, so it's within the block
    if (positions[i].top < lowestYOfBlock) {
      end = i;
      // the botom of this element is lower than the current known bottom of block
      if (positions[i].bottom > lowestYOfBlock) {
        lowestYOfBlock = positions[i].bottom;
      }
    }
    // the top of the element is below the overlap block, we are free.
    else {
      break;
    }
  }
  return end;
};

/**
 * Get blocks of overlapping elements. We only care if the content of the block overlaps, not the
 * margin. Margins can collapse together and they are not considered overlapping elements.
 */
export const detectOverlaps = (
  positions: ElementPosition[]
): OverlapBlock[] => {
  const blocks: OverlapBlock[] = [];

  positions.forEach((current, i) => {
    if (inOverlapBlock(i, blocks)) {
      // this has already been detected as part of an overlap block, keep going
      return;
    }
    const block = { start: i, end: i };

    // Compare the bottom of this element with the top of the element
    // below it. If they overlap (due to a CSS float), add
    // to the list of elements that have overlaps.
    if (positions[i + 1] && current.bottom > positions[i + 1].top) {
      block.start = findStart(i, positions);
      block.end = findEnd(i, positions);
    }

    if (block.start !== i || block.end !== i) {
      blocks.push(block);
    }
  });

  return blocks;
};
